import React, { useRef, useState } from 'react';
import { Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { newsletterApi } from '../services/api';

export function NewsletterSignupForm({ compact }) {
  const started = useRef(Date.now());
  const [f, setF] = useState({ firstName: '', email: '', consent: false, website: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!f.consent) return setError('Please tick the box to agree to receive PBB newsletters.');
    setBusy(true);
    try {
      const r = await newsletterApi.signup({ ...f, elapsedMs: Date.now() - started.current });
      setDone(r.message);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Please check your name and email and try again.');
    } finally { setBusy(false); }
  };

  if (done) return (
    <div className="text-center py-4" data-testid="nl-signup-success">
      <CheckCircle2 className="mx-auto text-green-600" size={40} />
      <div className="mt-3 text-lg font-semibold text-neutral-900">You're almost in!</div>
      <p className="mt-1 text-sm text-neutral-700">{done}</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="nl-signup-form">
      <input value={f.firstName} onChange={e => setF({ ...f, firstName: e.target.value })} required maxLength={60}
        className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-green-600" placeholder="First name" data-testid="nl-signup-first-name" />
      <input type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} required maxLength={200}
        className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-neutral-900 focus:outline-none focus:ring-2 focus:ring-green-600" placeholder="Email address" data-testid="nl-signup-email" />
      <input tabIndex={-1} autoComplete="off" value={f.website} onChange={e => setF({ ...f, website: e.target.value })}
        className="hidden" aria-hidden="true" name="website" data-testid="nl-signup-hp" />
      <label className="flex items-start gap-2 text-sm text-neutral-700">
        <input type="checkbox" checked={f.consent} onChange={e => setF({ ...f, consent: e.target.checked })} className="mt-1" data-testid="nl-signup-consent" />
        I agree to receive the weekly Promote Black Business newsletter by email. I can unsubscribe any time.
      </label>
      {error && <div className="text-sm text-red-600" data-testid="nl-signup-error">{error}</div>}
      <button type="submit" disabled={busy} data-testid="nl-signup-submit"
        className={`w-full rounded-lg bg-green-600 hover:bg-green-700 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 ${compact ? 'py-2.5' : 'py-3'}`}>
        {busy ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />} Join the newsletter
      </button>
    </form>
  );
}

export function JoinNewsletterButton({ className = '', testId = 'join-newsletter-btn', children }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={testId} className={className}>{children || <><Mail size={14} /> Join Our Weekly Newsletter</>}</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white text-neutral-900 border-neutral-200" data-testid="nl-signup-dialog">
          <DialogHeader>
            <DialogTitle className="text-neutral-900">Join Our Weekly Newsletter</DialogTitle>
            <DialogDescription className="text-neutral-600">Black business news, events, deals and community wins — once a week. No membership needed.</DialogDescription>
          </DialogHeader>
          <NewsletterSignupForm compact />
        </DialogContent>
      </Dialog>
    </>
  );
}
